/**
 * Form submission utilities for company forms
 * Consolidates error handling, success callbacks, and modal closing logic
 */

import { useCallback } from 'react'
import { useAppDispatch } from 'helpers/store'
import { toast } from 'utils/toastUtils'
import { closeModal, closeSecondaryModal } from 'helpers/actions'

export interface FormSubmitOptions {
  useSecondaryClose?: boolean
  inline?: boolean
  showSuccessToast?: boolean
  successMessage?: string
  errorMessage?: string
}

/**
 * Hook to handle form submission flow (validation, API call, close modal)
 */
export const useFormSubmitHandler = () => {
  const dispatch = useAppDispatch()

  const handleSubmitSuccess = useCallback(
    (options: FormSubmitOptions) => {
      const { useSecondaryClose = true, inline = false, showSuccessToast = true, successMessage } = options

      if (showSuccessToast && successMessage) {
        toast.success(successMessage)
      }

      if (!inline) {
        setTimeout(() => {
          if (useSecondaryClose) {
            dispatch(closeSecondaryModal())
          } else {
            dispatch(closeModal())
          }
        }, 0)
      }
    },
    [dispatch]
  )

  const handleSubmitError = useCallback((error: unknown, errorMessage: string) => {
    console.error('Form submission error:', error)
    toast.error(errorMessage)
  }, [])

  const handleCancel = useCallback(
    (options: FormSubmitOptions) => {
      const { useSecondaryClose = true } = options
      if (useSecondaryClose) {
        dispatch(closeSecondaryModal())
      } else {
        dispatch(closeModal())
      }
    },
    [dispatch]
  )

  return { handleSubmitSuccess, handleSubmitError, handleCancel }
}

/**
 * Utility function to wrap async form submission with loading and error handling
 */
export async function executeFormSubmission<T>(
  asyncFn: () => Promise<T>,
  options: {
    onSuccess?: (result: T) => void
    onError?: (error: Error) => void
    onFinally?: () => void
  } = {}
): Promise<T | null> {
  try {
    const result = await asyncFn()
    options.onSuccess?.(result)
    return result
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))
    options.onError?.(err)
    return null
  } finally {
    options.onFinally?.()
  }
}

/**
 * Utility to validate required fields in form data
 */
export function validateRequiredFields<T extends Record<string, any>>(
  data: T,
  requiredFields: (keyof T)[]
): string | null {
  for (const field of requiredFields) {
    if (!data[field] || (typeof data[field] === 'string' && !data[field].trim())) {
      return `${String(field)} is required`
    }
  }
  return null
}

/**
 * Utility to prepare form data for submission (trim strings, remove empty values)
 */
export function prepareFormData<T extends Record<string, any>>(
  data: T,
  options: {
    trimStrings?: boolean
    removeEmpty?: boolean
    excludeFields?: (keyof T)[]
  } = {}
): Partial<T> {
  const { trimStrings = true, removeEmpty = false, excludeFields = [] } = options
  const prepared: Partial<T> = {}

  for (const [key, value] of Object.entries(data)) {
    if (excludeFields.includes(key as keyof T)) continue

    let processedValue = value

    if (trimStrings && typeof value === 'string') {
      processedValue = value.trim()
    }

    if (removeEmpty && !processedValue) continue

    prepared[key as keyof T] = processedValue
  }

  return prepared
}

/**
 * Utility to handle modal close with optional data callback
 */
export function createModalCloseHandler<T>(
  dispatch: any,
  onSuccess?: (data?: T) => void,
  options: { useSecondaryClose?: boolean; inline?: boolean } = {}
) {
  return async (data?: T, shouldClose: boolean = false) => {
    onSuccess?.(data)

    if (shouldClose && !options.inline) {
      setTimeout(() => {
        if (options.useSecondaryClose !== false) {
          dispatch(closeSecondaryModal())
        } else {
          dispatch(closeModal())
        }
      }, 0)
    }
  }
}

/**
 * Utility to merge default form values with existing item data
 */
export function mergeFormDefaults<T extends Record<string, any>>(
  defaults: T,
  existing?: Partial<T>,
  overrides?: Partial<T>
): T {
  return {
    ...defaults,
    ...(existing || {}),
    ...(overrides || {}),
  } as T
}
